import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Landmark, Globe } from 'lucide-react';
import { STATE_LIST } from '../data/statePages';
import { REGION_LIST } from '../data/regionPages';
import { useCountry } from '../contexts/CountryContext';

const CALCS = [
  { key: 'US', to: '/tools/1099-tax-calculator', label: 'US 1099 Calculator' },
  { key: 'UK', to: '/tools/uk-self-assessment-tax-calculator', label: 'UK Self Assessment' },
  { key: 'CA', to: '/tools/canada-self-employed-tax-calculator', label: 'Canada (T2125)' },
  { key: 'DE', to: '/tools/germany-self-employed-tax-calculator', label: 'Germany (Steuer)' },
  { key: 'AU', to: '/tools/australia-sole-trader-tax-calculator', label: 'Australia Sole Trader' },
  { key: 'NZ', to: '/tools/new-zealand-sole-trader-tax-calculator', label: 'New Zealand Sole Trader' },
  { key: 'UAE', to: '/tools/uae-vat-calculator', label: 'UAE VAT' },
  { key: 'KSA', to: '/tools/saudi-arabia-vat-calculator', label: 'Saudi VAT' },
];

// Region guide slugs that belong to the Australia & New Zealand group.
const ANZ_SLUGS = ['self-employed-tax-australia', 'self-employed-tax-new-zealand'];

// Per-region copy + which guide groups are relevant.
const REGION_COPY = {
  US: { eyebrow: 'Freelancer tax in the US', h2: 'Tax guides for the United States', sub: 'Your free 1099 tax calculator plus a state-by-state self-employment tax guide.' },
  UK: { eyebrow: 'Freelancer tax in the UK', h2: 'Tax guides for the UK & Europe', sub: 'Your free Self Assessment calculator plus guides across the UK & Europe.' },
  CA: { eyebrow: 'Freelancer tax in Canada', h2: 'Tax tools for Canada', sub: 'Your free T2125 self-employed tax calculator, built for Canadian freelancers.' },
  DE: { eyebrow: 'Freelancer tax in Germany', h2: 'Tax guides for Germany & Europe', sub: 'Your free German tax calculator plus guides across Europe.' },
  AU: { eyebrow: 'Sole trader tax in Australia', h2: 'Tax tools for Australia & New Zealand', sub: 'Your free Australia sole trader tax calculator, plus a New Zealand guide next door.' },
  NZ: { eyebrow: 'Sole trader tax in New Zealand', h2: 'Tax tools for Australia & New Zealand', sub: 'Your free New Zealand sole trader tax calculator, plus an Australia guide next door.' },
  GLOBAL: { eyebrow: 'Freelancer tax, worldwide', h2: 'Tax guides for your country & state', sub: 'Not just the US. MoneyFeed keeps tax-ready books for freelancers across the US, UK & Europe — with a free guide for each.' },
};

const Pill = ({ to, children }) => (
  <Link
    to={to}
    className="px-3.5 py-1.5 bg-white rounded-full text-sm font-semibold text-[#064E3B] shadow-sm border border-gray-200 hover:border-[#84CC16] hover:-translate-y-0.5 transition-all"
    data-testid={`tax-guide-link-${to.replace(/\//g, '-')}`}
  >
    {children}
  </Link>
);

const Group = ({ icon, title, items, render }) => (
  <div>
    <h3 className="flex items-center justify-center gap-2 text-base font-bold text-[#064E3B] mb-4">
      {icon} {title}
    </h3>
    <div className="flex flex-wrap justify-center gap-2">{items.map(render)}</div>
  </div>
);

const TaxGuidesSection = () => {
  const { region } = useCountry();
  const [showAll, setShowAll] = useState(false);

  const copy = REGION_COPY[region] || REGION_COPY.GLOBAL;
  const isGlobal = region === 'GLOBAL' || showAll;
  const showStates = isGlobal || region === 'US';
  const showEurope = isGlobal || region === 'UK' || region === 'DE';
  const showAnz = isGlobal || region === 'AU' || region === 'NZ';

  const anzList = REGION_LIST.filter((r) => ANZ_SLUGS.includes(r.slug));
  const europeList = REGION_LIST.filter((r) => !ANZ_SLUGS.includes(r.slug));

  // Lead the calculators with the selected region's calculator.
  const calcs = [...CALCS].sort((a, b) => (a.key === region ? -1 : b.key === region ? 1 : 0));

  return (
    <section id="tax-guides" className="py-16 md:py-20" data-testid="tax-guides-section">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#84CC16]/15 border border-[#84CC16]/40 text-[#3f6212] text-xs font-bold uppercase tracking-wider mb-4">
          <Globe className="h-4 w-4" /> {copy.eyebrow}
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-[#064E3B] mb-3">{copy.h2}</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">{copy.sub}</p>
      </div>

      <div className="max-w-5xl mx-auto px-4 space-y-10">
        <Group
          icon={<Calculator className="h-5 w-5 text-[#84CC16]" />}
          title="Free tax calculators"
          items={calcs}
          render={(c) => <Pill key={c.to} to={c.to}>{c.label}</Pill>}
        />
        {showStates && (
          <Group
            icon={<Landmark className="h-5 w-5 text-[#84CC16]" />}
            title="United States — by state"
            items={STATE_LIST}
            render={(s) => <Pill key={s.slug} to={`/${s.slug}`}>{s.state}</Pill>}
          />
        )}
        {showEurope && (
          <Group
            icon={<Globe className="h-5 w-5 text-[#84CC16]" />}
            title="UK &amp; Europe — by country"
            items={europeList}
            render={(r) => <Pill key={r.slug} to={`/${r.slug}`}>{r.place === 'the Netherlands' ? 'Netherlands' : r.place}</Pill>}
          />
        )}
        {showAnz && (
          <Group
            icon={<Globe className="h-5 w-5 text-[#84CC16]" />}
            title="Australia &amp; New Zealand"
            items={anzList}
            render={(r) => <Pill key={r.slug} to={`/${r.slug}`}>{r.place}</Pill>}
          />
        )}

        {!isGlobal && (
          <div className="text-center">
            <button
              onClick={() => setShowAll(true)}
              data-testid="tax-guides-show-all"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#3f6212] hover:text-[#064E3B] hover:underline transition-colors"
            >
              <Globe className="h-4 w-4" /> Browse all countries &amp; states
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default TaxGuidesSection;
